/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {memo, MouseEventHandler} from 'react';
import {Checkbox} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import clsx from 'clsx';
import {dlsColor} from '../theme/theme';
import {useThemeContext} from '../theme/useThemeContext';

const CHECKED_ICON_BACKGROUND_IMAGE =
  'url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\'%3E%3Cpath fill-rule=\'evenodd\' clip-rule=\'evenodd\' d=\'M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z\' fill=\'%23fff\'/%3E%3C/svg%3E")';

interface StyleProps {
  checked?: boolean;
};

const useLegacyCheckboxStyles = makeStyles(() => ({
  gridHeaderCheckbox : {
    padding    : 0,
    marginLeft : '-4px',
    '& svg'    : {
      fontSize : '1.34rem',
      color    : dlsColor('grey3')
    }
  },
  checked : {
    '& svg' : {
      color : ({checked}: StyleProps) => dlsColor(checked ? 'caBlue' : 'grey3')
    },
    '&:hover' : {
      backgroundColor : 'transparent'
    }
  }
}));

const useCurrentCheckboxStyles = makeStyles(() => ({
  gridHeaderCheckbox : {
    padding    : 0,
    marginLeft : '-4px',
    '&:hover'  : {
      backgroundColor : 'transparent'
    }
  },
  icon : {
    width        : 16,
    height       : 16,
    display      : 'inline-block',
    boxSizing    : 'border-box',
    border       : `2px solid ${dlsColor('grey1')}`,
    borderRadius : 2
  },
  checkedIcon : {
    position        : 'relative',
    borderColor     : dlsColor('grey1'),
    backgroundColor : dlsColor('caBlue'),
    '&:before'      : {
      top              : '50%',
      left             : '50%',
      width            : 16,
      height           : 16,
      content          : '""',
      display          : 'block',
      position         : 'absolute',
      transform        : 'translate(-50%, -50%)',
      backgroundImage  : CHECKED_ICON_BACKGROUND_IMAGE,
      backgroundSize   : 'contain',
      backgroundRepeat : 'no-repeat'
    }
  },
  indeterminateIcon : {
    '&:before' : {
      backgroundColor : dlsColor('white'),
      backgroundImage : 'none',
      borderRadius    : 1,
      content         : '""',
      display         : 'block',
      height          : 2,
      left            : '50%',
      position        : 'absolute',
      top             : '50%',
      transform       : 'translate(-50%, -50%)',
      width           : 9
    }
  }
}));

interface CheckboxProps extends StyleProps {
  indeterminate: boolean;
  onClick?: MouseEventHandler;
}

/**
 * Ion styled checkbox built for re-use.
 *
 * ```typescript
 *
 *  import {IonCheckbox} from '@ion/dls/src/grid/';
 *
 *  <IonCheckbox onClick={onClick}
 *    checked={true}
 *    indeterminate={false} />
 *
 * ```
 */
export const IonCheckbox = ({
  onClick,
  checked,
  indeterminate
}: CheckboxProps) => {
  const {isCurrent}    = useThemeContext();
  const legacyClasses  = useLegacyCheckboxStyles({checked});
  const currentClasses = useCurrentCheckboxStyles();

  const currentThemeProps = {
    classes : {
      root : currentClasses.gridHeaderCheckbox
    },
    checkedIcon       : <span className={clsx(currentClasses.icon, currentClasses.checkedIcon)} />,
    indeterminateIcon : (
      <span className={clsx(currentClasses.icon, currentClasses.checkedIcon, currentClasses.indeterminateIcon)} />
    ),
    icon : <span className={currentClasses.icon} />
  };

  const legacyThemeProps = {
    classes : {
      root           : legacyClasses.gridHeaderCheckbox,
      colorSecondary : legacyClasses.checked
    }
  };

  return (
    <Checkbox disableRipple
      onClick={onClick}
      size='small'
      checked={checked}
      indeterminate={indeterminate}
      {...(isCurrent ? currentThemeProps : legacyThemeProps)} />
  );
};

export default memo(IonCheckbox);
